import React, { useEffect, useState } from 'react';

import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import pluralize from 'utils/pluralize';
import { ACTIVATION, DOMAINCONFIG, USERS } from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Text, Tooltip } from 'components/DataDisplay';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import { Tabs } from 'components/Navigation';
import { SearchField } from 'components/SearchField';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalDelete } from '../../../components/ModalDelete';

import { ModalForm } from './components/ModalForm';

const startLevel = 1;
const PropertiesContainer = () => {
  const [users, setUsers] = useState([]);
  const [shopGroups, setShopGroups] = useState([]);
  const [shops, setShops] = useState([]);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openModal, setModal] = useState(null);
  const [selected, setSelected] = useState({});
  const [currentLevel, setCurrentLevel] = useState(startLevel);
  const [levels, setLevels] = useState([]);
  const [domainCountries, setDomainCountries] = useState([]);
  const { themeColors } = useTheme();
  const { adminRights } = useUser();

  const closeModal = () => {
    setModal(null);
    setSelected({});
  };

  const defineHierarchy = (items, parentKey = '', currLevel = startLevel) => {
    const hierarchyGr = [];

    items.forEach(item => {
      if (item.parent === parentKey || (parentKey === '' && !item.parent)) {
        const childHierarchy = defineHierarchy(items, item.key, currLevel + 1);
        // eslint-disable-next-line no-param-reassign
        item.levelFromShop = 1;
        if (childHierarchy.length > 0) {
          // eslint-disable-next-line no-param-reassign
          item.children = childHierarchy;
          // eslint-disable-next-line no-param-reassign
          item.levelFromShop =
            Math.max(...childHierarchy.map(child => child.levelFromShop)) + 1;
        }

        // eslint-disable-next-line no-param-reassign
        item.level = currLevel;
        hierarchyGr.push(item);
      }
    });

    return hierarchyGr;
  };

  const selectShopGroup = id => {
    const group = shopGroups.find(el => el._id === id);
    setSelected(group);
    return group;
  };

  const getToolTipText = txt => {
    return (
      <Row spacing={0}>
        <Item>
          <Text color={themeColors.dark}>{txt}</Text>
        </Item>
      </Row>
    );
  };

  const formatUsers = userList => {
    const nbUsers = userList.length;
    if (nbUsers === 0) {
      return <Text>Aucun</Text>;
    }
    if (nbUsers === 1) {
      return <Text color="pml"> {userList[0] && userList[0].email} </Text>;
    }

    const others = nbUsers - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      'utilisateur',
      others
    )}`;

    const tooltipContent = () => {
      return (
        <Container>
          {userList.map(user => {
            return getToolTipText(user.email);
          })}
        </Container>
      );
    };
    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color="pml"> {userList[0].email}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color="pml">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const formatShops = shopList => {
    const count = shopList.length;
    if (count === 0) {
      return <Text>Aucun</Text>;
    }
    if (count === 1) return <Text color="pml"> {shopList[0]} </Text>;

    const others = count - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      'magasins',
      others
    )}`;

    const tooltipContent = () => {
      return (
        <Container>{shopList.map(shop => getToolTipText(shop))}</Container>
      );
    };
    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color="pml"> {shopList[0]}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color="pml">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const getDatas = async () => {
    const shopsList = await ACTIVATION.getActivationShops();
    const userslist = await USERS.getAll();
    const shopGroupList = await ACTIVATION.getActivationShopGoups();
    const domain = await DOMAINCONFIG.get();
    const hierarchyLevels = defineHierarchy(shopGroupList);
    setUsers(userslist);
    setShops(shopsList);
    setShopGroups(shopGroupList);
    setDomainCountries(domain.countries || []);
    const domainLevels = domain.groupTaxonomy.reduce((acc, taxo) => {
      if (taxo.level !== 'shop') {
        acc.push({
          ...taxo,
          label: taxo.name,
          onClick: () => setCurrentLevel(parseInt(taxo.level, 10))
        });
      }

      return acc;
    }, []);
    domainLevels.sort((a, b) => {
      return a.level.localeCompare(b.level);
    });

    const lowestLevel = domainLevels.reduce((lowest, taxo) => {
      const currLvl = parseInt(taxo.level, 10);
      if (lowest === null || currLvl < lowest) {
        return currLvl;
      }
      return lowest;
    }, null);

    const lastTaxonomyLevel = parseInt(
      domainLevels[domainLevels.length - 1]?.level,
      10
    );
    const hasFinalChilds = hierarchy => {
      if (hierarchy?.level === lastTaxonomyLevel) {
        return true;
      }
      if (!hierarchy?.children?.length) {
        return false;
      }
      return hierarchy.children.reduce((acc, ele) => {
        return acc || hasFinalChilds(ele);
      }, false);
    };

    hierarchyLevels.forEach(hierarchy => {
      const isRoot = hasFinalChilds(hierarchy);
      // eslint-disable-next-line no-param-reassign
      hierarchy.isRoot = isRoot;
    });

    const flattenedHierarchy = hierarchyLevels.reduce((acc, group) => {
      const flattenGroups = (groups, currLevel) => {
        groups.forEach(subGroup => {
          acc.push({ ...subGroup, level: currLevel });
          if (subGroup.children) {
            flattenGroups(subGroup.children, currLevel + 1);
          }
        });
      };

      flattenGroups([group], group.level);
      return acc;
    }, []);

    setLevels(domainLevels);

    const mapped = shopGroupList.map(item => {
      const newItem = { ...item };
      const matchedGroup = flattenedHierarchy.find(
        group => group._id === item._id
      );
      newItem.level = matchedGroup?.level || lowestLevel;

      const foundUsers = item.users.reduce((acc, curr) => {
        const exist = userslist.find(el => el._id === curr);
        if (exist) {
          acc.push(exist);
        }
        return acc;
      }, []);

      const foundShops = item.shops.reduce((acc, curr) => {
        const exist = shopsList.find(el => el._id === curr);
        if (exist) {
          acc.push(exist.name);
        }
        return acc;
      }, []);
      newItem.users = formatUsers(foundUsers);
      newItem.shops = formatShops(foundShops);
      newItem.shopsName = foundShops;
      return newItem;
    });

    setRows(mapped);
    setAllRows(mapped);

    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  const openCreate = async () => {
    if (adminRights?.pml?.shopGroup?.create) {
      setModal('modalCreate');
    } else {
      setModal('unauthorized');
    }
  };

  const deleteGroup = id => {
    if (adminRights?.general?.connection?.delete) {
      selectShopGroup(id);
      setModal('modalDelete');
    } else {
      setModal('unauthorized');
    }
  };

  const onCreate = async data => {
    await ACTIVATION.createActivationShopGoup({
      ...data
    });

    closeModal();
    setLoaded(false);
  };

  const onDelete = async () => {
    await ACTIVATION.deleteActivationShopGoup(selected._id);
    closeModal();
    setLoaded(false);
  };

  const openUpdate = id => {
    if (adminRights?.pml?.shopGroup?.update) {
      selectShopGroup(id);

      setModal('modalUpdate');
    } else {
      setModal('unauthorized');
    }
  };

  const onUpdate = async data => {
    await ACTIVATION.updateActivationShopGoup(selected._id, {
      ...selected,
      ...data
    });

    closeModal();
    setLoaded(false);
  };

  return (
    <Container>
      <Row spacing={4}>
        <Item xs={3}>
          <SearchField
            onChange={setRows}
            datas={allRows}
            titleHead="Chercher un groupe"
            placeholder="Nom du groupe"
          />
        </Item>
        <Item justify="flex-end" xs>
          <ButtonCreate onClick={openCreate}>Ajouter un groupe</ButtonCreate>
          {openModal === 'modalCreate' && (
            <ModalForm
              open={openModal === 'modalCreate'}
              onClose={closeModal}
              onCreate={onCreate}
              config={{
                shops,
                users,
                shopGroups: allRows,
                levels,
                domainCountries
              }}
            />
          )}
        </Item>
      </Row>
      <Row spacing={4}>
        <Item flex>
          <Tabs tabs={levels} />
        </Item>
      </Row>
      <Row spacing={4}>
        <Item>
          <Loading loading={!loaded} />
          {loaded && (
            <TableData
              rows={rows.filter(
                row => !levels.length || row.level === currentLevel
              )}
              headers={[
                { label: 'Key', id: 'key', sortKey: 'key' },
                { label: 'Nom', id: 'name', sortKey: 'name' },
                { label: 'Utilisateurs', id: 'users' },
                { label: 'Magasins', id: 'shops' }
              ]}
              hidenFields={['_id']}
              onUpdate={openUpdate}
              onDelete={deleteGroup}
            />
          )}
          {openModal === 'modalUpdate' && (
            <ModalForm
              open={openModal === 'modalUpdate'}
              onClose={closeModal}
              onCreate={onUpdate}
              config={{
                shops,
                users,
                selected,
                shopGroups: allRows,
                levels,
                domainCountries
              }}
            />
          )}
          {openModal === 'modalDelete' && (
            <ModalDelete
              name={selected.name}
              type="ce groupe"
              open={openModal === 'modalDelete'}
              onClose={closeModal}
              onDelete={onDelete}
            />
          )}

          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
        </Item>
      </Row>
    </Container>
  );
};

export default PropertiesContainer;
